/**
 * Меню и заказ еды/напитков (UC-G.09) — /menu* — router-резолвер по роли
 * и подпути (design-guest v7 §10/§11/§12).
 *
 * РОЛЬ-РОУТИНГ (Артём, 2026-06-16): один поддомен на отель.
 *   - Менеджер/владелец (canManage) → УПРАВЛЕНИЕ меню (§9): обзор/категории/
 *     позиции, CRUD. Заявку через меню НЕ создаёт.
 *   - Гость/персонал → route-based поток заказа (design-guest v7):
 *       §10 /menu            — плитки категорий (GuestMenuCategories);
 *       §11 /menu/c/{code}   — позиции категории (GuestMenuCategory);
 *       §12 /menu/order      — корзина и подтверждение (GuestMenuOrder).
 *
 * Источник данных гостя — витрина каталога услуг (GET /v1/service-catalog),
 * отфильтрованная до F&B-категорий. Серверной корзины НЕТ: корзина
 * клиентская (useCart), на оформлении каждая позиция уходит отдельной
 * заявкой POST /v1/requests (через useCreateOrder). Корзина — sticky-CTA
 * «Перейти к заказу · N · ₽» (намеренно НЕ модалка/экран-список).
 */
import { useLocation } from 'react-router-dom';
import { MenuManager } from './MenuManager.js';
import { MenuOverview } from './MenuOverview.js';
import { MenuCategories } from './MenuCategories.js';
import { GuestMenuCategories } from './GuestMenuCategories.js';
import { GuestMenuCategory } from './GuestMenuCategory.js';
import { GuestMenuOrder } from './GuestMenuOrder.js';

export interface MenuPageProps {
  /**
   * Управляющая роль (owner/manager). true → менеджерские экраны меню
   * (design §9), без корзины и без заявок. false/undefined → гостевой
   * заказ в номер (route-based §10/§11/§12).
   */
  canManage?: boolean;
  /**
   * Какой менеджерский экран меню показать (design §9): по умолчанию обзор
   * (9.1). 'categories' → 9.2, 'items' → 9.3 (полный CRUD позиций). Гость
   * этот проп игнорирует.
   */
  view?: 'overview' | 'categories' | 'items';
}

export function MenuPage({ canManage = false, view }: MenuPageProps): JSX.Element {
  const { pathname } = useLocation();

  // Менеджер/владелец видят управление меню (design §9), а не корзину гостя.
  if (canManage) {
    const resolved =
      view ??
      (pathname.endsWith('/categories')
        ? 'categories'
        : pathname.endsWith('/items')
          ? 'items'
          : 'overview');
    if (resolved === 'categories') return <MenuCategories />;
    if (resolved === 'items') return <MenuManager canManage={canManage} />;
    return <MenuOverview />;
  }

  // Гость: route-based поток меню (design-guest v7 §10/§11/§12).
  // /menu/order → §12; /menu/c/{code} → §11; иначе /menu → §10.
  if (pathname.endsWith('/order') || pathname.includes('/menu/order')) {
    return <GuestMenuOrder />;
  }
  if (pathname.includes('/menu/c/')) {
    return <GuestMenuCategory />;
  }
  return <GuestMenuCategories />;
}
